{
	"title": "Voice tutor",
	"subtitle": "Voice mode",
	"intro": {
		"title": "How the voice tutor differs",
		"body": "This is a conversation: you speak out loud, you are heard and answered. The tutor in chat replies in text, an audio recap only reads the material out.",
		"bullets": [
			"A two-way conversation — unlike an audio recap",
			"By voice, not by text — unlike the chat",
			"Follows a lesson plan or targets a single gap"
		]
	},
	"lessonPlan": {
		"title": "Lesson plan",
		"topics": "Topics",
		"generating": "Preparing lesson plan…"
	},
	"session": {
		"ready": "Ready",
		"starting": "Starting…",
		"active": "In session",
		"paused": "Paused",
		"ending": "Ending…",
		"ended": "Session ended",
		"duration": "Duration: {time}"
	},
	"actions": {
		"start": "Start session",
		"pause": "Pause",
		"resume": "Resume",
		"end": "End",
		"interrupt": "Interrupt"
	},
	"error": {
		"startFailed": "Could not start the session. Please try again.",
		"endFailed": "Could not end the session cleanly.",
		"planNotReady": "Wait until the lesson plan is ready"
	},
	"indicators": {
		"listening": "Listening",
		"thinking": "Thinking",
		"speaking": "Speaking",
		"audioOff": "Audio off"
	},
	"settings": {
		"voice": "Voice",
		"speed": "Speech rate",
		"language": "Session language",
		"topic": "Topic, subject or lesson wishes",
		"duration": "Session duration",
		"tutorVoice": "Tutor voice",
		"speechSpeed": "Speech rate · {speed}×",
		"saveTranscript": "Save <0>text transcript</0> (NOT audio) after the session",
		"saveTranscriptHint": "The transcript helps revisit key moments. Audio is still not recorded.",
		"generateLessonPlan": "Generate lesson plan",
		"lessonPlanTitle": "Lesson plan · 4 steps",
		"lessonPlanAiBadge": "Plan generated by AI. You can adjust it by voice during the session."
	},
	"mic": {
		"aria": {
			"idle": "Turn on voice mode",
			"connecting": "Connecting",
			"listening": "Microphone active — mute",
			"muted": "Microphone muted — unmute",
			"error": "Connection error — reconnect"
		}
	},
	"conn": {
		"connecting": "Connecting",
		"connected": "Live",
		"networkDrop": "Connection unstable",
		"paused": "Paused",
		"ended": "Ended"
	},
	"transcript": {
		"title": "Conversation transcript",
		"empty": "Lines will appear here once the session begins.",
		"speakerUser": "Me",
		"speakerTutor": "Tutor"
	},
	"recentSessions": {
		"title": "Recent sessions",
		"empty": "Your past voice sessions will appear here."
	},
	"plan": {
		"step1": "Intro: key concepts of the topic",
		"step2": "Deep explanation through examples",
		"step3": "Comprehension check: questions from AI",
		"step4": "Summary and control questions"
	},
	"compliance": {
		"audioNotStored": "Audio is not stored on the server",
		"transcriptOnly": "Only the text transcript is saved",
		"audioNotStoredShort": "Audio isn't stored"
	},
	"disclaimer": {
		"title": "Before starting the voice session",
		"body": "The voice session uses AI. Your speech is processed in real time and is **not stored** on our servers. AI can make mistakes — verify important facts.",
		"checkbox": "I understand: speech is processed by AI in real time and is not stored",
		"confirm": "Start session",
		"cancel": "Cancel"
	},
	"consent": {
		"title": "Before starting the voice session",
		"body": "The voice session uses AI. Speech is processed in real time; audio is not stored by us or by the AI service.",
		"required": "I consent to my speech being processed by an AI service.",
		"saveTranscript": "Save the text transcript of the lesson (optional)",
		"start": "Start session",
		"cancel": "Cancel",
		"requireAgree": "You must confirm the consent above to start the session."
	},
	"live": {
		"fatal": {
			"expiredTitle": "Session unavailable",
			"failedTitle": "Session ended with an error",
			"expiredBody": "This voice session can't be resumed (token expired or session closed). Start a new one.",
			"failedBody": "The AI connection was interrupted. Try starting a new session.",
			"back": "Back to session setup"
		},
		"header": {
			"end": "End",
			"endAria": "End session"
		},
		"mobilePlan": {
			"trigger": "Plan {done}/{total}",
			"triggerEmpty": "Lesson plan",
			"title": "Lesson plan",
			"openAria": "Open lesson plan",
			"empty": "Preparing the lesson plan — usually ~10 seconds."
		},
		"controls": {
			"mic": "Microphone",
			"mute": "Mute",
			"unmute": "Unmute",
			"reconnect": "Reconnect",
			"micOff": "Turn off mic",
			"micOn": "Turn on mic",
			"speakerOff": "Turn off sound",
			"speakerOn": "Turn on sound"
		},
		"states": {
			"connecting": "Connecting to the tutor and microphone…",
			"errorBody": "Could not connect to the tutor. Check microphone access and whether the server is running.",
			"reconnect": "Reconnect",
			"closed": "The tutor connection is closed (server unavailable or session ended).",
			"backToSetup": "Back to setup",
			"connected": "Speak into the mic — Tutor is listening and will respond. You can interrupt any time.",
			"muted": "Mic off",
			"preparing": "Preparing the session…"
		},
		"toast": {
			"micDenied": "No microphone access. Allow access and try again.",
			"timeUp": "Session time is up — ending the lesson.",
			"sessionEnded": "Session ended — transcript saved."
		},
		"defaultTopic": "Voice session",
		"yourTurn": "Your turn — answer out loud",
		"tutorSpeaking": "tutor is answering — you can interrupt",
		"focusMode": "Focus",
		"showTranscript": "Show transcript",
		"currentStep": "Now",
		"quickReply": {
			"understood": "Got it",
			"explainAgain": "Explain again",
			"next": "Next"
		},
		"quickReplyLabel": "Quick reply",
		"toolStatus": {
			"searching": "Searching materials…",
			"visual": "Preparing diagram…",
			"working": "Working…"
		},
		"visual": {
			"dismiss": "Dismiss diagram"
		},
		"jumpInstruction": "[Lesson control] The student picked the section “{title}” from the plan. Smoothly switch to this section: briefly acknowledge the move and continue the explanation focused on it. Keep replying in the same language the lesson is in.",
		"jumpToast": "Switching to “{title}”"
	},
	"history": {
		"backAria": "Back",
		"back": "Back",
		"statusFailed": "Interrupted",
		"statusEnded": "Ended",
		"transcriptCaption": "Past session transcript",
		"transcriptEmpty": "Transcript was not saved. The session may have ended too early or transcript saving was turned off.",
		"speakerUser": "Me",
		"speakerTutor": "Tutor",
		"lessonPlanHeading": "Lesson plan",
		"resume": "Resume",
		"resumeConfirmTitle": "Session ended",
		"resumeConfirmBody": "This voice session has already ended. Resume it and continue the conversation where you left off?"
	},
	"endConfirm": {
		"title": "End the session?",
		"descriptionWithTime": "Session has been running for <0>{minutes} min</0>. Your plan progress will be saved.",
		"description": "Your plan progress will be saved.",
		"continue": "Continue session",
		"end": "End"
	},
	"plannerSidebar": {
		"title": "Lesson plan",
		"progressLabel": "Steps completed",
		"collapseAria": "Collapse",
		"expandAria": "Expand",
		"empty": "Preparing the lesson plan — usually ~10 seconds.",
		"jumpHint": "Jump to this section"
	},
	"wizard": {
		"title": "New voice session",
		"step.source": "Source",
		"step.plan": "Plan",
		"step.voice": "Voice",
		"step1Hint": "Pick a material or upload a new one — AI builds the lesson plan from it.",
		"step2Hint": "Pick a level and a length, then generate the lesson plan before you start.",
		"step3Hint": "Pick language and tutor voice. You can preview before starting.",
		"selectMaterials": "Select materials",
		"materialsSelected": "{count, plural, one {# material selected} other {# materials selected}}",
		"collectionSelected": "Folder: {name}",
		"orTopic": "or set a topic without materials",
		"regeneratePlan": "Regenerate plan",
		"back": "Back",
		"next": "Next",
		"cancel": "Cancel",
		"level": {
			"title": "Level",
			"hint": "The level sets how deep the lesson goes; the age sets the tutor’s wording and tone. Optional."
		}
	},
	"idle": {
		"tagline": "Talk to AI out loud — it knows your material and keeps the lesson plan.",
		"topicPlaceholder": "Krebs cycle, photosynthesis, set theory…",
		"materialLabel": "Material",
		"materialNone": "No material",
		"materialSearch": "Find material…",
		"materialHint": "The AI will be grounded in chunks of the selected material and cite specific places.",
		"durationMinutes": "{count, plural, one {# minute} other {# minutes}}",
		"planGenerating": "AI is building the lesson plan…",
		"planFailed": "Could not generate the plan",
		"planRetry": "Retry",
		"planReady": "Lesson plan · {count, plural, one {# step} other {# steps}}",
		"quotaExhausted": "Voice tutor minutes are used up. Upgrade your plan to continue.",
		"lockedCta": "Available on Plus / Pro",
		"lockedHint": "Voice tutor is not available on Free. Plus / Pro — up to 120 min/month.",
		"planRequiredCta": "Generate a lesson plan first",
		"lockedCtaNoSale": "Not available yet",
		"lockedHintNoSale": "The voice tutor isn't available in this app yet.",
		"quotaExhaustedNoSale": "Voice tutor minutes are used up for now."
	},
	"voices": {
		"Aoede": "Warm, friendly",
		"Charon": "Calm, academic",
		"Kore": "Confident, clear",
		"Puck": "Energetic, lively",
		"Fenrir": "Deep, expressive",
		"Zephyr": "Bright, light",
		"Leda": "Youthful, clean",
		"Orus": "Firm, confident",
		"Callirrhoe": "Relaxed, measured",
		"Autonoe": "Friendly, open",
		"Enceladus": "Soft, unhurried",
		"Iapetus": "Low, meditative",
		"Umbriel": "Dark, calm",
		"Algieba": "Velvety, narrative",
		"Despina": "Melodic, smooth",
		"Erinome": "Reserved, professional",
		"Algenib": "Strong, emotional",
		"Rasalgethi": "Even, objective",
		"Laomedeia": "Playful, light",
		"Achernar": "Bright, clear",
		"Alnilam": "Balanced, neutral",
		"Schedar": "Crisp, mentoring",
		"Gacrux": "Deep, trustworthy",
		"Pulcherrima": "Graceful, expressive",
		"Achird": "Gentle, empathetic",
		"Zubenelgenubi": "Lively, conversational",
		"Vindemiatrix": "Analytical, precise",
		"Sadachbia": "Bright, upbeat",
		"Sadaltager": "Knowledgeable, authoritative",
		"Sulafat": "Calm, refined"
	},
	"voicePicker": {
		"preview": "Preview voice",
		"stop": "Stop",
		"placeholder": "Choose a voice"
	},
	"list": {
		"subtitle": "{count, plural, one {# voice session} other {# voice sessions}}",
		"newSession": "New session",
		"continue": "Continue",
		"searchPlaceholder": "Search sessions…",
		"errorTitle": "Couldn’t load sessions",
		"retry": "Retry",
		"emptyDescription": "Start a voice session — the tutor knows your material.",
		"backToList": "Back to sessions",
		"stageProgress": "Stage {stage} of {total}"
	}
}
